import { useState, useEffect } from 'react';

export default function HeroSection() {
  const [scanProgress, setScanProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setScanProgress((prev) => (prev >= 100 ? 0 : prev + 1));
    }, 30);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative w-full h-screen overflow-hidden bg-black">
      {/* Background Video/Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=massive%20automotive%20manufacturing%20assembly%20line%20with%20robotic%20arms%20welding%20car%20frames%2C%20industrial%20scale%20factory%20floor%20with%20heavy%20machinery%20and%20automated%20systems%2C%20overhead%20360%20degree%20panoramic%20view%20showing%20entire%20production%20line%2C%20dramatic%20industrial%20lighting%20with%20sparks%20flying%2C%20ultra%20realistic%20professional%20industrial%20photography%2C%20high%20detail%20manufacturing%20environment%20with%20workers%20in%20safety%20gear%20operating%20advanced%20equipment&width=1920&height=1080&seq=hero-factory-001&orientation=landscape"
          alt="Industrial Manufacturing Line"
          className="w-full h-full object-cover object-top"
        />
        {/* Dark Overlay for Contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
      </div>

      {/* Scanning Laser Effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Horizontal Scan Lines */}
        <div 
          className="absolute w-full h-1 bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent opacity-80 blur-sm"
          style={{
            top: `${scanProgress}%`,
            boxShadow: '0 0 20px #00d4ff, 0 0 40px #00d4ff'
          }}
        ></div>
        <div 
          className="absolute w-full h-1 bg-gradient-to-r from-transparent via-[#ff6b35] to-transparent opacity-60 blur-sm"
          style={{
            top: `${(scanProgress + 30) % 100}%`,
            boxShadow: '0 0 20px #ff6b35, 0 0 40px #ff6b35'
          }}
        ></div>

        {/* Wireframe Grid Overlay */}
        <svg className="absolute inset-0 w-full h-full opacity-30" style={{ mixBlendMode: 'screen' }}>
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#00d4ff" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Data Points Rising */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              backgroundColor: i % 2 === 0 ? '#00d4ff' : '#ff6b35',
              boxShadow: `0 0 10px ${i % 2 === 0 ? '#00d4ff' : '#ff6b35'}`,
              animation: `float ${3 + Math.random() * 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-8 lg:px-16">
          <div className="max-w-4xl">
            {/* Logo */}
            <div className="mb-8">
              <h1 className="text-3xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                Multimodal AI Lab
              </h1>
              <p className="text-sm text-[#00d4ff] tracking-widest font-semibold">Powered by Jolly Good</p>
            </div>

            {/* Main Catchcopy */}
            <div className="mb-8">
              <h2 className="text-6xl lg:text-7xl font-black leading-tight mb-6" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                <span className="block text-white mb-2">現場の「全て」が、</span>
                <span className="block bg-gradient-to-r from-[#00d4ff] via-[#00d4ff] to-[#ff6b35] bg-clip-text text-transparent">
                  最強のAI教師データになる。
                </span>
              </h2>
            </div>

            {/* Sub Copy */}
            <div className="mb-12">
              <p className="text-2xl text-gray-300 leading-relaxed font-bold" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                人間の視野の<span className="text-[#00d4ff] text-3xl font-black">12倍</span>。<br />
                「空間ビッグデータ」基盤へようこそ。
              </p>
            </div>

            {/* Stats Bar */}
            <div className="mb-12 flex items-center gap-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#00d4ff]/20 border border-[#00d4ff] flex items-center justify-center">
                  <i className="ri-360-line text-2xl text-[#00d4ff]"></i>
                </div>
                <div>
                  <div className="text-2xl font-black text-white">360°</div>
                  <div className="text-xs text-gray-400">全方位キャプチャ</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#ff6b35]/20 border border-[#ff6b35] flex items-center justify-center">
                  <i className="ri-database-2-line text-2xl text-[#ff6b35]"></i>
                </div>
                <div>
                  <div className="text-2xl font-black text-white">12x</div>
                  <div className="text-xs text-gray-400">情報量</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#4ade80]/20 border border-[#4ade80] flex items-center justify-center">
                  <i className="ri-brain-line text-2xl text-[#4ade80]"></i>
                </div>
                <div>
                  <div className="text-2xl font-black text-white">AI解析</div>
                  <div className="text-xs text-gray-400">リアルタイム</div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <button className="group relative px-10 py-5 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-black text-lg font-black rounded-lg hover:shadow-[0_0_40px_rgba(0,212,255,0.6)] hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <span className="flex items-center gap-3">
                  無料デモを見る
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
                </span>
              </button>
              <button className="px-10 py-5 bg-transparent text-white text-lg font-bold rounded-lg border-2 border-white/30 hover:border-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all duration-300 whitespace-nowrap cursor-pointer">
                資料をダウンロード
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-gray-400 tracking-widest font-bold">SCROLL</span>
          <i className="ri-arrow-down-line text-2xl text-[#00d4ff]"></i>
        </div>
      </div>

      {/* Floating Animation Keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-100px) translateX(20px);
            opacity: 0.8;
          }
        }
      `}</style>
    </section>
  );
}
